import React from "react";
import { Button, Card, CardBody, CardHeader, Col, Collapse, Row } from "reactstrap";
import FeatherIcon from "feather-icons-react";
import { MONTHLY_CHARGE_KEYS, money, rentBreakdownFromItem, totalRentFromItem } from "../PadInfo/padInfoTypes";

// Visual tokens — the PAD modal's, so the tenant form reads as one product.
const C = {
  primaryMd: "#1A6FB5",
  border: "#E4E9F2",
  inputBorder: "#D6DEEA",
  muted: "#64748B",
  text: "#0F1F35",
  soft: "#F7F9FC",
};

const CHARGE_KEYS = new Set(MONTHLY_CHARGE_KEYS.flatMap((k) => [k.key, ...(k.aliases || [])]));

const isChargeField = (f) => CHARGE_KEYS.has(f.key) || f.key === "targetRent" || /fee$/i.test(f.key);
const isLeaseField = (f) => f.type === "date" || /lease|deposit|^sd$|movein|moveout/i.test(f.key);
const isResidentField = (f) => /tenant|occupant|resident|phone|email|contact|name/i.test(f.key);

const fieldRank = (field, cluster) => {
  const key = String(field.key || "").toLowerCase();

  if (cluster === "Resident") {
    if (/^tenants?$|^residents?$/.test(key)) return 10;
    if (/name/.test(key)) return 20;
    if (/phone/.test(key)) return 30;
    if (/email/.test(key)) return 40;
  }

  if (cluster === "Lease & dates") {
    if (/^sd$|security.*deposit|deposit/.test(key)) return 10;
    if (/lease.*start|movein/.test(key)) return 20;
    if (/lease.*end|moveout/.test(key)) return 30;
  }

  if (cluster === "Monthly charges") {
    if (/basic.*rent|base.*rent/.test(key)) return 10;
    if (/target.*rent/.test(key)) return 20;
    if (/^ac|telus/.test(key)) return 30;
    if (/storage|dog|cat|ss.*fee|ug.*fee/.test(key)) return 40;
  }

  if (cluster === "Other") {
    if (/style/.test(key)) return 10;
    if (/layout/.test(key)) return 20;
    if (/sq.*ft|square/.test(key)) return 30;
    if (/^ac$/.test(key)) return 40;
    if (/view/.test(key)) return 50;
    if (/unit.*no|unitno/.test(key)) return 60;
    if (/psf/.test(key)) return 70;
  }

  return 100;
};

const orderClusterFields = (fields, cluster) =>
  [...fields].sort((left, right) => fieldRank(left, cluster) - fieldRank(right, cluster));

// Keep each dynamic cluster balanced, even when its configurable field count is uneven.
export function balanceRows(items, maxItemsPerRow = 4) {
  if (!items.length) return [];

  const rowCount = Math.ceil(items.length / maxItemsPerRow);
  const itemsPerRow = Math.floor(items.length / rowCount);
  const rowsWithOneExtra = items.length % rowCount;
  const rows = [];
  let cursor = 0;

  for (let rowIndex = 0; rowIndex < rowCount; rowIndex += 1) {
    const rowSize = itemsPerRow + (rowIndex < rowsWithOneExtra ? 1 : 0);
    rows.push(items.slice(cursor, cursor + rowSize));
    cursor += rowSize;
  }

  return rows;
}

/**
 * Assign the property's dynamic field layout into display clusters. Order within a cluster
 * is the layout's own; a field matching nothing lands in Other, so no layout loses a field.
 * Exported for tests.
 */
export function clusterFields(fields) {
  const clusters = [
    { title: "Resident", icon: "user", fields: [] },
    { title: "Lease & dates", icon: "calendar", fields: [] },
    { title: "Monthly charges", icon: "dollar-sign", fields: [] },
    { title: "Other", icon: "grid", fields: [] },
  ];
  for (const f of fields || []) {
    if (isChargeField(f)) clusters[2].fields.push(f);
    else if (isLeaseField(f)) clusters[1].fields.push(f);
    else if (isResidentField(f)) clusters[0].fields.push(f);
    else clusters[3].fields.push(f);
  }
  return clusters.map((cluster) => ({
    ...cluster,
    fields: orderClusterFields(cluster.fields, cluster.title),
  }));
}

/**
 * The Tenant Basic Information card: the dynamic layout's fields, clustered and restyled,
 * with the live Total Rent named in the Monthly charges cluster title. Field rendering
 * stays the caller's (DynamicField, passed in as `renderField`) — this component only
 * arranges and dresses it.
 */
const BasicInfoSection = ({ fields, values, open, onToggle, hasErrors, renderField }) => {
  const clusters = clusterFields(fields).filter((c) => c.fields.length);
  // Live from the form values, over the same keys the mastersheet's Total Rent column and
  // the PAD module sum — the three always agree. Nothing is stored.
  const totalRent = totalRentFromItem(values || {});
  const breakdown = rentBreakdownFromItem(values || {});

  return (
    <Card className="tbi-card mb-3">
      <style>{`
        .tbi-card { border-color: ${C.border}; }
        .tbi-card .card-header { padding: 0.75rem 1.25rem; background: #fff; border-bottom: 1px solid ${C.border}; text-align: center; }
        .tbi-section-button { font-weight: 700; }
        .tbi-head .ic, .tbi-head .err, .tbi-head .chev { display: none; }
        .tbi-head .t { font-size: inherit; font-weight: inherit; }
        .tbi-summary { font-size: 12px; font-weight: 500; color: ${C.muted}; text-align: center; margin: -0.25rem 0 1rem; }
        .tbi-cluster-title .sub { font-weight: 600; text-transform: none; letter-spacing: 0; color: ${C.text}; }
        .tbi-body { padding: 1.25rem; }
        .tbi-cluster + .tbi-cluster { margin-top: 1.25rem; }
        .tbi-cluster-title { display: flex; align-items: center; gap: 7px; font-size: 11px; font-weight: 700; letter-spacing: .08em; text-transform: uppercase; color: ${C.muted}; border-bottom: 1px solid ${C.border}; padding-bottom: 6px; margin-bottom: 12px; }
        .tbi-field-row + .tbi-field-row { margin-top: 0.25rem; }
        .tbi-card label { font-size: 12px; font-weight: 600; color: ${C.text}; margin-bottom: 5px; }
        .tbi-card .form-control, .tbi-card .form-select { min-height: 38px; font-size: 13px; color: ${C.text}; border: 1px solid ${C.inputBorder}; border-radius: 9px; }
        .tbi-card .form-control:focus, .tbi-card .form-select:focus { border-color: ${C.primaryMd}; box-shadow: 0 0 0 2px rgba(26,111,181,0.12); }
        .tbi-card .form-control:disabled, .tbi-card .form-control[readonly] { background: ${C.soft}; color: ${C.muted}; }
        .tbi-card .form-text { font-size: 11px; color: ${C.muted}; }
        .tbi-card .invalid-feedback { font-size: 11px; }
      `}</style>

      <CardHeader className="py-3 text-center">
      <Button
        type="button"
        color={hasErrors ? "danger" : "primary"}
        outline
        className="tbi-head tbi-section-button px-4 py-2 rounded-pill"
        onClick={onToggle}
        aria-expanded={!!open}
      >
        <FeatherIcon icon="user" className="me-2" size={16} />
        <span className="t">Tenant Basic Information</span>
        {hasErrors && (
          <span className="err"><FeatherIcon icon="alert-circle" size={13} /> Needs attention</span>
        )}
        <span className="chev"><FeatherIcon icon={open ? "chevron-up" : "chevron-down"} size={17} /></span>
      </Button>
      </CardHeader>

      <Collapse isOpen={!!open}>
        <CardBody className="tbi-body">
          <div className="tbi-summary">{(fields || []).length} fields · Total Rent {totalRent === null ? "—" : money(totalRent)}</div>
          {clusters.map((cluster) => (
            <div className="tbi-cluster" key={cluster.title}>
              <div
                className="tbi-cluster-title"
                title={cluster.title === "Monthly charges" && breakdown.length ? breakdown.map((r) => `${r.label} ${money(r.amount)}`).join(" + ") : undefined}
              >
                <FeatherIcon icon={cluster.icon} size={13} />
                {cluster.title}
                {cluster.title === "Monthly charges" && (
                  <span className="sub">(Total Rent - {totalRent === null ? "—" : money(totalRent)})</span>
                )}
              </div>
              {balanceRows(cluster.fields).map((row, rowIndex) => (
                <Row className="tbi-field-row" key={`${cluster.title}-${rowIndex}`}>
                  {row.map((field) => (
                    <Col md={12 / row.length} key={field.key}>
                      {renderField(field)}
                    </Col>
                  ))}
                </Row>
              ))}
            </div>
          ))}
        </CardBody>
      </Collapse>
    </Card>
  );
};

export default BasicInfoSection;
